import React, { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  Divider,
  Link,
  Typography,
} from '@mui/material';
import { startBatchContentGeneration } from '../services/tutorQuizService';
import TutorContentReviewQueue from '../components/tutor/quiz/TutorContentReviewQueue';
import TutorBatchJobProgress from '../components/tutor/quiz/TutorBatchJobProgress';

export default function TutorContentReviewPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const cId = Number(courseId);

  const [activeJobId, setActiveJobId] = useState<number | null>(null);
  const [generating, setGenerating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  async function handleStartGeneration() {
    setGenerating(true);
    try {
      const job = await startBatchContentGeneration(cId);
      setActiveJobId(job.jobId!);
    } catch {
      /* error surfaced via service */
    } finally {
      setGenerating(false);
    }
  }

  const handleJobComplete = useCallback(() => {
    setActiveJobId(null);
    setRefreshKey(k => k + 1);
  }, []);

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link underline="hover" sx={{ cursor: 'pointer' }} onClick={() => navigate('/tutor/courses')}>
          My Courses
        </Link>
        <Typography color="text.primary">Content Review</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Content Review Queue</Typography>
        <Button
          variant="contained"
          disabled={generating || activeJobId !== null}
          onClick={handleStartGeneration}
        >
          {generating ? 'Starting…' : 'Generate Content for Empty Topics'}
        </Button>
      </Box>

      {activeJobId && (
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>Content generation in progress…</Typography>
          <TutorBatchJobProgress courseId={cId} jobId={activeJobId} onComplete={handleJobComplete} />
          <Divider sx={{ mt: 2 }} />
        </Box>
      )}

      <TutorContentReviewQueue key={refreshKey} courseId={cId} />
    </Box>
  );
}
